import { Box, Button } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { replacePiiDeleted } from '../../../assets/utils/replacePiiDeleted';
import { useToastNotificationContext } from '../../../hooks/global/alert/useToastNotification';
import {
  clearPiiRemovedDescriptionPositions,
  setPiiRemovedDescriptionPositions,
} from '../../../slices/piiRemovalSlice';
import { RootState } from '../../../utils/store';

const PiiRemovalTextBox = () => {
  const dispatch = useDispatch();
  const { createToastNotification } = useToastNotificationContext();

  const removedDescriptionRanges = useSelector(
    (state: RootState) => state.piiRemoval.piiRemovedDescriptionRanges,
  );

  const { description } = useSelector(
    (state: RootState) => state.reportData.report,
  );

  const [highlightedText, setHighlightedText] = useState<string>('');

  const applyHighlight = useCallback(() => {
    let removedText = '';
    let lastIndex = 0;
    const positions = removedDescriptionRanges
      ? [...removedDescriptionRanges]
      : [];

    positions.sort((a, b) => a[0] - b[0]);

    positions.forEach((range) => {
      if (!range) return;
      const [start, end] = range;
      removedText += description.substring(lastIndex, start);
      removedText += `<span style="background-color: red">${description.substring(
        start,
        end,
      )}</span>`;
      lastIndex = end;
    });

    removedText += description.substring(lastIndex);

    const replacedText = replacePiiDeleted(removedText);
    return replacedText;
  }, [removedDescriptionRanges, description]);

  useEffect(() => {
    setHighlightedText(applyHighlight());
  }, [removedDescriptionRanges, applyHighlight]);

  const handleHighlight = () => {
    const selection = window.getSelection();
    if (selection) {
      const range = selection.getRangeAt(0);
      const startContainer = range.startContainer;
      const endContainer = range.endContainer;

      const piiDeletedRanges: [number, number][] = [];
      const piiDeletedRegex = /\*PII Deleted\*/g;
      let match;

      while ((match = piiDeletedRegex.exec(description)) !== null) {
        piiDeletedRanges.push([match.index, match.index + match[0].length]);
      }

      // Get the full text content
      const fullText = description;

      // Calculate the start and end positions
      let startPosition = range.startOffset;
      let endPosition = range.endOffset;

      // Count characters before the start position
      if (startContainer && startContainer.nodeType === Node.TEXT_NODE) {
        const startText = (startContainer as Text).textContent || '';
        startPosition += fullText.indexOf(startText);
      }

      // Count characters before the end position
      if (endContainer && endContainer.nodeType === Node.TEXT_NODE) {
        const endText = (endContainer as Text).textContent || '';
        endPosition += fullText.indexOf(endText);
      }

      // Check if the selected range overlaps with any existing highlighted positions
      const overlappingPosition = removedDescriptionRanges
        ? removedDescriptionRanges.find(
          ([start, end]) =>
            (start <= startPosition && end >= startPosition) ||
              (start <= endPosition && end >= endPosition),
        )
        : null;

      const checkForDeletedPositions = piiDeletedRanges.find(
        ([start, end]) =>
          (start <= startPosition && end >= startPosition) ||
          (start <= endPosition && end >= endPosition),
      );
      if (overlappingPosition) {
        createToastNotification(
          'This area has already been selected to be removed.',
          'error',
        );
      } else if (checkForDeletedPositions) {
        createToastNotification('You cannot delete PII Deleted text.', 'error');
      } else if (
        (startPosition <= 1 && endPosition <= 1) ||
        startPosition == null ||
        endPosition == null
      ) {
        createToastNotification(
          'Pii Removal Not Complete: Ensure you select and highlight the text you want to remove within the report description.',
          'error',
        );
        startPosition = 0;
        endPosition = 0;
      } else {
        // Create a new array with updated positions
        const updatedPositions: [number, number] = [startPosition, endPosition];

        if (updatedPositions !== undefined) {
          // Dispatch an action to update the Redux state
          dispatch(setPiiRemovedDescriptionPositions(updatedPositions));
        }
      }
    }
  };

  const handleRemoveHighlights = () => {
    const highlightedSpans = document.querySelectorAll('.highlight');
    highlightedSpans.forEach((span) => {
      const parent = span.parentNode;
      if (parent) {
        parent.replaceChild(document.createTextNode(span.textContent!), span);
      }
    });
    // Create a new array with updated positions
    dispatch(clearPiiRemovedDescriptionPositions());
  };

  return (
    <Box>
      <Box
        contentEditable={false}
        sx={{
          border: '1px solid black',
          padding: '12px',
          borderRadius: '6px',
          minHeight: '100px',
          cursor: 'text',
          overflowY: 'auto',
          maxHeight: '200px',
        }}
        dangerouslySetInnerHTML={{ __html: highlightedText }}
      />
      <Box sx={{ mt: '12px' }}>
        <Button
          onClick={handleHighlight}
          sx={{
            textTransform: 'none',
            color: 'rgba(93, 93, 93, 1)',
            fontWeight: 600,
            fontSize: '14px',
            backgroundColor: 'transparent',
            '&:hover': {
              backgroundColor: 'transparent',
            },
          }}
        >
          <FormattedMessage defaultMessage='Highlight for Removal' />
        </Button>
        <Button
          onClick={handleRemoveHighlights}
          sx={{
            textTransform: 'none',
            color: 'rgba(93, 93, 93, 1)',
            fontWeight: 600,
            fontSize: '14px',
            backgroundColor: 'transparent',
            '&:hover': {
              backgroundColor: 'transparent',
            },
          }}
        >
          <FormattedMessage defaultMessage='Remove all highlights' />
        </Button>
      </Box>
      <style>
        {`
        .highlight {
          background-color: red;
          color: black;
        }
      `}
      </style>
    </Box>
  );
};

export default PiiRemovalTextBox;
